export const ENDORSEMENT_TRANSITION_STATUSES = ['DRAFT', 'REVIEW', 'FINAL', 'ARCHIVED'] as const;

export type EndorsementTransitionStatus = (typeof ENDORSEMENT_TRANSITION_STATUSES)[number];

type EndorsementTransitionsByStatus = Record<EndorsementTransitionStatus, EndorsementTransitionStatus[]>;

const ENDORSEMENT_USER_TRANSITIONS_WITH_REVIEW: EndorsementTransitionsByStatus = {
  ARCHIVED: ['DRAFT'],
  DRAFT: ['REVIEW', 'ARCHIVED'],
  REVIEW: ['FINAL', 'DRAFT', 'ARCHIVED'],
  FINAL: [],
};

const ENDORSEMENT_ADMIN_TRANSITIONS_WITH_REVIEW: EndorsementTransitionsByStatus = {
  ARCHIVED: ['DRAFT'],
  DRAFT: ['REVIEW', 'ARCHIVED'],
  REVIEW: ['FINAL', 'DRAFT', 'ARCHIVED'],
  FINAL: ['DRAFT', 'REVIEW'],
};

// Legacy matrix used when the EndorsementReview feature flag is off — Draft transitions directly
// to Final without any Review step. REVIEW still has exits so an endorsement already parked there
// (flag flipped off after the fact, or entered via an API path that doesn't respect the flag) can
// still be moved out — mirrors how FIRM_ORDER keeps its exits when FirmOrderStatus is disabled.
const ENDORSEMENT_USER_TRANSITIONS_NO_REVIEW: EndorsementTransitionsByStatus = {
  ARCHIVED: ['DRAFT'],
  DRAFT: ['FINAL', 'ARCHIVED'],
  REVIEW: ['DRAFT', 'FINAL', 'ARCHIVED'],
  FINAL: [],
};

const ENDORSEMENT_ADMIN_TRANSITIONS_NO_REVIEW: EndorsementTransitionsByStatus = {
  ARCHIVED: ['DRAFT'],
  DRAFT: ['FINAL', 'ARCHIVED'],
  REVIEW: ['DRAFT', 'FINAL', 'ARCHIVED'],
  FINAL: ['DRAFT'],
};

const isEndorsementTransitionStatus = (status?: string): status is EndorsementTransitionStatus =>
  ENDORSEMENT_TRANSITION_STATUSES.includes(status as EndorsementTransitionStatus);

type EndorsementTransitionArgs = {
  sourceStatus?: string;
  isAdmin?: boolean;
  isEndorsementReviewEnabled?: boolean;
};

export const getAllowedEndorsementTargetStatuses = ({
  sourceStatus,
  isAdmin,
  isEndorsementReviewEnabled = true,
}: EndorsementTransitionArgs): EndorsementTransitionStatus[] => {
  if (!isEndorsementTransitionStatus(sourceStatus)) {
    return [];
  }

  const transitions = isEndorsementReviewEnabled
    ? isAdmin
      ? ENDORSEMENT_ADMIN_TRANSITIONS_WITH_REVIEW
      : ENDORSEMENT_USER_TRANSITIONS_WITH_REVIEW
    : isAdmin
      ? ENDORSEMENT_ADMIN_TRANSITIONS_NO_REVIEW
      : ENDORSEMENT_USER_TRANSITIONS_NO_REVIEW;

  return transitions[sourceStatus];
};

export const canTransitionEndorsementStatus = ({
  sourceStatus,
  targetStatus,
  isAdmin,
  isEndorsementReviewEnabled = true,
}: EndorsementTransitionArgs & { targetStatus?: string }) => {
  if (!isEndorsementTransitionStatus(targetStatus)) {
    return false;
  }

  if (sourceStatus === targetStatus) {
    return true;
  }

  return getAllowedEndorsementTargetStatuses({ sourceStatus, isAdmin, isEndorsementReviewEnabled }).includes(
    targetStatus,
  );
};
